import { ForkEffect } from 'redux-saga/effects';
import { call, delay, getContext, put, race, select, take, takeLatest } from 'typed-redux-saga';
import { RoutineTimelineActions } from './routineTimelineSlice';
import { selectRoutineTimeline, selectRoutineTimelineTaskId } from './routineTimelineSelectors';
import {
  ROUTINE_REPORTS_MAX_RETRIES,
  ROUTINE_REPORTS_RETRY_INTERVAL_MS,
  STOPPED_RUN_REPORTS_MAX_RETRIES,
  STOPPED_RUN_REPORTS_RETRY_INTERVAL_MS,
} from './utils/constants';
import { getMissingReportRouteIndexes } from './utils/routineProgress';
import { getRoutineReports, getRoutineTimelineData } from './utils/routineTimeline';
import type {
  OpenRoutineTimelineRequestAction,
  RefreshRoutineReportsRequestAction,
} from './routineTimelineActions.types';
import type { IState } from 'app/cross-cutting-concerns/state-management/interfaces/IState';
import type { IDependencies } from 'app/cross-cutting-concerns/dependency-injection/interfaces/IDependencies';

// Raced against a close, so a close while the lists load can't write into the next robot's timeline.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* openRoutineTimelineSaga(action: OpenRoutineTimelineRequestAction): Generator<unknown, void, any> {
  const { robotCleaningControlService } = yield* getContext<IDependencies>('dependencies');
  const { materialNumber, serialNumber } = action.payload;

  try {
    const { data, closed } = yield* race({
      data: call(getRoutineTimelineData, robotCleaningControlService, materialNumber, serialNumber),
      closed: take(RoutineTimelineActions.closeRoutineTimeline),
    });

    if (closed) {
      return;
    }

    if (!data) {
      yield* put(RoutineTimelineActions.routineTimelineUnavailable());

      return;
    }

    yield* put(RoutineTimelineActions.openRoutineTimelineSuccess(data));
  } catch (error) {
    console.error(error);
    yield* put(RoutineTimelineActions.openRoutineTimelineError({ error }));
  }
}

// A finished route's report is saved just after the run moves on to the next step, so it may not be there yet.
// The reports are read again, waiting between reads, up to ROUTINE_REPORTS_MAX_RETRIES times while any finished route
// has none. A newer request (the next step) replaces this one (takeLatest), which starts the count again. A route
// still without its report after that is marked unavailable. A failed read counts as a retry.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function* refreshRoutineReportsSaga(action: RefreshRoutineReportsRequestAction): Generator<unknown, void, any> {
  const { robotCleaningControlService } = yield* getContext<IDependencies>('dependencies');
  const { materialNumber, serialNumber, finishedCount, isStoppedEarly = false } = action.payload;
  const maxRetries = isStoppedEarly ? STOPPED_RUN_REPORTS_MAX_RETRIES : ROUTINE_REPORTS_MAX_RETRIES;
  const retryIntervalMs = isStoppedEarly ? STOPPED_RUN_REPORTS_RETRY_INTERVAL_MS : ROUTINE_REPORTS_RETRY_INTERVAL_MS;
  const taskId = yield* select(selectRoutineTimelineTaskId);

  if (!taskId) {
    return;
  }

  const selectMissingRouteIndexes = (state: IState): number[] => {
    const { reports, unavailableRouteIndexes } = selectRoutineTimeline(state);

    // A stopped run asks again for routes given up on earlier, as the robot status moved on since.
    return getMissingReportRouteIndexes({
      reports,
      finishedCount,
      unavailableRouteIndexes: isStoppedEarly ? [] : unavailableRouteIndexes,
    });
  };

  for (let retry = 0; retry <= maxRetries; retry += 1) {
    const missingRouteIndexes = yield* select(selectMissingRouteIndexes);

    if (missingRouteIndexes.length === 0) {
      return;
    }

    // Raced against the close, so a close while waiting or reading can't write into the next robot's timeline.
    if (retry > 0) {
      const { closedWhileWaiting } = yield* race({
        wait: delay(retryIntervalMs),
        closedWhileWaiting: take(RoutineTimelineActions.closeRoutineTimeline),
      });

      if (closedWhileWaiting) {
        return;
      }
    }

    try {
      const { reports, closed } = yield* race({
        reports: call(getRoutineReports, robotCleaningControlService, materialNumber, serialNumber, taskId),
        closed: take(RoutineTimelineActions.closeRoutineTimeline),
      });

      if (closed || !reports) {
        return;
      }

      yield* put(RoutineTimelineActions.routineReportsReceived(reports));
    } catch (error) {
      console.error(error);
    }
  }

  const stillMissingRouteIndexes = yield* select(selectMissingRouteIndexes);

  if (stillMissingRouteIndexes.length > 0) {
    yield* put(RoutineTimelineActions.routeReportsUnavailable(stillMissingRouteIndexes));
  }
}

export function* routineTimelineSaga(): Generator<ForkEffect<never>, void> {
  yield* takeLatest(RoutineTimelineActions.openRoutineTimelineRequest, openRoutineTimelineSaga);
  yield* takeLatest(RoutineTimelineActions.refreshRoutineReportsRequest, refreshRoutineReportsSaga);
}
